import { gsap } from 'gsap';
import _debounce from 'lodash.debounce';
import Vue from 'vue';
import Errors from '../classes/errors';
import { setCaptchaEuScript } from '../utils';

Vue.component('renting-option-form-component', {
    props: {
        siteHandle: {
            value: String,
        },
        siteId: {
            value: String,
        },
        cdnUrl: {
            type: String,
        },
        modelSlug: {
            type: String,
        },
        captchaEuPublicKey: {
            type: String,
        },
        arvalAgreementId: {
            type: String,
        },
        pageContent: {
            type: Object,
        },
        isPrivateRentingProp: {
            type: String,
            required: true,
        },
        stores: {
            value: Array,
        },
        isCaptchaEuEnabled: {
            value: Boolean,
        },
    },

    data() {
        return {
            errors: new Errors(),
            formCompleted: false,
            submitting: false,

            configuration: {},
            selectedEdition: this.pageContent.rentingCarEditions[0],
            selectedExteriorColor: null,
            selectedInteriorColor: null,

            countryHandle: '',
            calculatedPriceFromApi: 0.0,
            legalDescriptionFromApi: '',
            months: 36,
            formattedMileage: '10 000 kms',
            mileage: 10000,
            isFormVisible: false,
            downPaymentEntry: 'no',
            downPayment: 0,
            isDownPaymentError: false,

            apiCarPrices: undefined,
            isApiLoading: true,
            isPrivateRenting: false,
        };
    },

    computed: {
        configurationJson() {
            return JSON.stringify(this.configuration);
        },
        compatibleInteriorColors() {
            if (!this.selectedExteriorColor) return [];
            return this.selectedEdition.interiorColors.filter((color) =>
                this.selectedExteriorColor.interiorColorSlugs.includes(
                    color.slug,
                ),
            );
        },
        disabledButton() {
            const isDownPaymentEmpty =
                this.downPayment === 0 && this.downPaymentEntry === 'yes';
            const isColorSelectionIncomplete = !this.selectedExteriorColor; // Only exterior color is mandatory
            const isButtonDisabled =
                this.isApiLoading ||
                this.isDownPaymentError ||
                isDownPaymentEmpty ||
                isColorSelectionIncomplete;
            return isButtonDisabled ? 'renting-option__button--disabled' : '';
        },
        toggleLoadingButtonClass() {
            return this.submitting ? 'button--loading' : '';
        },
        editions() {
            return this.pageContent.rentingCarEditions;
        },
        isDownPaymentInputVisible() {
            return this.downPaymentEntry === 'yes' && !this.isFormVisible;
        },
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },
    },

    mounted() {
        this.isPrivateRenting = this.isPrivateRentingProp === 'true';

        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }
        setCaptchaEuScript();
        this.showSelectedEditionDetails(this.selectedEdition.carEditionSlug);

        // Select the first exterior color by default
        if (
            this.selectedEdition.exteriorColors &&
            this.selectedEdition.exteriorColors.length > 0
        ) {
            this.selectExteriorColor(this.selectedEdition.exteriorColors[0]);
        }

        this.$watch(
            () => this.$root.csrfTokenValue,
            (newCsrfTokenValue) => {
                if (!newCsrfTokenValue) return;
                this.handleArvalPriceRecalculation(
                    this.selectedEdition.carManufacturerCode,
                    this.selectedEdition.co2Emission,
                    this.selectedEdition.modelPrice,
                    this.months,
                    this.mileage,
                );
            },
        );
    },

    watch: {
        downPaymentEntry(newVal) {
            if (newVal === 'no') {
                this.downPayment = 0;
                return;
            }

            setTimeout(() => {
                this.watchDownPaymentValueChange();
            }, 10);
        },

        downPayment(newVal) {
            this.debouncedHandleArvalPriceRecalculation(
                this.selectedEdition.carManufacturerCode,
                this.selectedEdition.co2Emission,
                this.selectedEdition.modelPrice,
                this.months,
                this.mileage,
                newVal,
            );
        },

        months(newVal) {
            this.handleArvalPriceRecalculation(
                this.selectedEdition.carManufacturerCode,
                this.selectedEdition.co2Emission,
                this.selectedEdition.modelPrice,
                newVal,
                null,
                this.downPayment,
            );
        },

        mileage(newVal) {
            this.handleArvalPriceRecalculation(
                this.selectedEdition.carManufacturerCode,
                this.selectedEdition.co2Emission,
                this.selectedEdition.modelPrice,
                null,
                newVal,
                this.downPayment,
            );
        },

        selectedEdition: {
            deep: true,
            handler(newValue) {
                if (!newValue) return;
                this.showSelectedEditionDetails(newValue.carEditionSlug);
                this.handleArvalPriceRecalculation(
                    newValue.carManufacturerCode,
                    newValue.co2Emission,
                    newValue.modelPrice,
                    null,
                    null,
                );
            },
        },

        isFormVisible(newVal) {
            if (newVal) return;
        },
    },

    methods: {
        watchDownPaymentValueChange() {
            const downPaymentInput = document.getElementById('downPayment');
            if (!downPaymentInput) return;

            downPaymentInput.addEventListener('input', (e) => {
                if (isNaN(e.target.value)) {
                    this.downPayment = 0;
                    return;
                }
                this.downPayment =
                    e.target.value.length > 0 ? e.target.value : 0;
            });
        },
        slugifyEdition(edition) {
            return edition.replace(/\s+/g, '-').toLowerCase();
        },

        selectEdition(edition) {
            this.selectedEdition = edition;

            // Select the first exterior color by default when edition changes
            if (edition.exteriorColors && edition.exteriorColors.length > 0) {
                this.selectExteriorColor(edition.exteriorColors[0]);
            }
        },

        parseTransformedImage(image) {
            return JSON.parse(image);
        },

        getSelectedEditionStyle(editionHandle) {
            const slugifiedSelectedEdition = this.slugifyEdition(
                this.selectedEdition.carEdition,
            );
            const slugifiedEditionHandle = this.slugifyEdition(editionHandle);
            return slugifiedSelectedEdition === slugifiedEditionHandle
                ? 'renting-option-car__edition__button--selected'
                : '';
        },

        showSelectedEditionDetails(editionHandle) {
            const allEditionHandlesWithoutSelected =
                this.pageContent.rentingCarEditions.filter(
                    (edition) => edition.carEditionSlug !== editionHandle,
                );

            const selectedButtonExtraText = document.querySelector(
                `#${editionHandle} > .renting-option-car__edition__button__extra-text`,
            );
            const allElements = allEditionHandlesWithoutSelected.map(
                ({ carEditionSlug }) =>
                    document.querySelector(
                        `#${carEditionSlug} > .renting-option-car__edition__button__extra-text`,
                    ),
            );

            const timeline = gsap.timeline();
            if (!selectedButtonExtraText) return;
            timeline
                .to(allElements, {
                    height: 0,
                    duration: 0,
                })
                .to(selectedButtonExtraText, {
                    height: 'auto',
                    duration: 0,
                });
        },

        handleDownPaymentEntrySelect(e) {
            this.downPaymentEntry = e.target.value;
        },

        handleMonthsSelect(e) {
            this.months = Number(e.target.value);
        },

        formatMileage(mileage) {
            return (
                mileage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') +
                ' kms'
            );
        },

        handleMileageSelect(e) {
            this.mileage = Number(e.target.value);
            this.formattedMileage = this.formatMileage(e.target.value);
        },

        showForm() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.isFormVisible = true;
            this.configuration = {
                carEdition: this.selectedEdition.carEdition,
                carEditionSlug: this.selectedEdition.carEditionSlug,
                carManufacturerCode: this.selectedEdition.carManufacturerCode,
                carPrice: this.showCarPrice(
                    JSON.stringify(this.selectedEdition),
                    this.selectedEdition.modelPrice,
                ),
            };
        },

        hideForm() {
            this.isFormVisible = false;
        },

        formatPrice(input) {
            if (typeof input !== 'string') return ''; // Ensure input is a string

            // Replace commas with dots for decimal and remove other formatting
            const sanitized = input.replace(/[^\d,.]/g, '').replace(/,/g, '.');

            // Split the sanitized input by dots
            const parts = sanitized.split('.');

            if (parts.length > 1) {
                // Handle multiple dots: keep the first part as the integer, last part as the decimals
                const integerPart = parts.slice(0, -1).join(''); // Combine all but the last part
                const decimalPart = parts[parts.length - 1]; // The last part is the decimals
                return `${integerPart}.${decimalPart}`;
            }

            return Number(sanitized); // Return the sanitized input if no dot/commas were found
        },

        showCarPrice(carModel, carPrice) {
            const decodedCarModel = JSON.parse(carModel);
            if (!this.apiCarPrices) return carPrice;
            const foundPrice = this.apiCarPrices.find(
                (apiCarPrice) =>
                    apiCarPrice.carManufacturerCode ===
                    decodedCarModel.carManufacturerCode,
            ).apiCarPrice;
            return foundPrice;
        },

        prepareColorOptions() {
            const options = [];
            if (this.selectedExteriorColor) {
                options.push({
                    manufacturerCode:
                        this.selectedExteriorColor.manufacturerCode,
                    description: this.selectedExteriorColor.name,
                    isOEMoption: this.selectedExteriorColor.isOemOption,
                    optionPrice: {
                        priceExclTax:
                            this.selectedExteriorColor.priceExclTax || 0,
                    },
                });
            }
            if (this.selectedInteriorColor) {
                options.push({
                    manufacturerCode:
                        this.selectedInteriorColor.manufacturerCode,
                    description: this.selectedInteriorColor.name,
                    isOEMoption: this.selectedInteriorColor.isOemOption,
                    optionPrice: {
                        priceExclTax:
                            this.selectedInteriorColor.priceExclTax || 0,
                    },
                });
            }
            return options;
        },

        async handleArvalPriceRecalculation(
            carManufacturerCode,
            co2Emission,
            modelPrice,
            newMonths,
            newMileage,
            downPayment,
        ) {
            this.isApiLoading = true;
            try {
                this.errors.clear();
                this.isDownPaymentError = false;

                const options = this.prepareColorOptions();
                const requestData = {
                    vehicle: {
                        carManufacturerCode: carManufacturerCode,
                        co2Emission: co2Emission,
                        modelPrice: modelPrice,
                    },
                    agreementId: this.arvalAgreementId,
                    months: newMonths ?? this.months,
                    kilometers: newMileage ?? this.mileage,
                    downPayment: {
                        amountExclTax: downPayment
                            ? downPayment
                            : this.downPayment,
                    },
                };

                if (options.length > 0) {
                    requestData.vehicle.options = options;
                }

                const { data } = await axios.post(
                    '/v1/renting/recalculate-car-prices',
                    requestData,
                    {
                        headers: {
                            'X-CSRF-TOKEN': this.$root.csrfTokenValue,
                        },
                    },
                );

                this.calculatedPriceFromApi = !this.isPrivateRenting
                    ? data.proposal.leasePrice.priceExclTax
                    : data.proposal.leasePrice.priceInclTax;
                this.legalDescriptionFromApi =
                    data.proposal.legalNotice.shortDescription ??
                    data.proposal.legalNotice.longDescriptionUrl ??
                    '';
            } catch (e) {
                console.log({ ...e });
            } finally {
                this.isApiLoading = false;
            }
        },

        debouncedHandleArvalPriceRecalculation: _debounce(function (
            carManufacturerCode,
            co2Emission,
            modelPrice,
            newMonths,
            newMileage,
            downPayment,
        ) {
            const fullCarPrice = Number(this.selectedEdition.modelPrice);
            const fifteenPercentOfCarPrice = (fullCarPrice * 15) / 100;
            const formattedDownPaymentValue = this.formatPrice(downPayment);
            if (formattedDownPaymentValue > fifteenPercentOfCarPrice) {
                this.isDownPaymentError = true;
                this.errors.record({
                    downPayment: [
                        'A downpayment of 15% of the car price is required',
                    ],
                });
                return;
            }

            this.handleArvalPriceRecalculation(
                carManufacturerCode,
                co2Emission,
                modelPrice,
                newMonths,
                newMileage,
                downPayment,
            );
        },
        300),

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let dataFromForm = new FormData(form);
            const data = {};

            for (let [key, val] of dataFromForm.entries()) {
                Object.assign(data, { [key]: val });
            }

            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!this.isCaptchaEuEnabled) {
                    await this.handleDataSubmit(form, captchaSolution);
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(form, captchaSolution);
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        selectExteriorColor(color) {
            this.selectedExteriorColor = color;
            this.selectedInteriorColor = null; // Reset interior color when exterior color changes

            // Select first compatible interior color
            const compatibleColors = this.selectedEdition.interiorColors.filter(
                (interiorColor) =>
                    this.selectedExteriorColor.interiorColorSlugs.includes(
                        interiorColor.slug,
                    ),
            );
            if (compatibleColors.length > 0) {
                this.selectedInteriorColor = compatibleColors[0];
            }

            EventBus.$emit('renting-set-interior-color', {
                color: this.selectedInteriorColor,
                isInitial: true,
            });
            this.recalculatePrice();
        },

        selectInteriorColor(color) {
            if (this.selectedInteriorColor?.slug === color.slug) return;

            this.selectedInteriorColor = color;
            this.recalculatePrice();
            EventBus.$emit('renting-set-interior-color', {
                color: this.selectedInteriorColor,
                isInitial: false,
            });
        },

        isExteriorColorSelected(slug) {
            return this.selectedExteriorColor?.slug === slug;
        },

        isInteriorColorSelected(slug) {
            return this.selectedInteriorColor?.slug === slug;
        },

        async recalculatePrice() {
            const exteriorColorPrice =
                this.selectedExteriorColor?.priceExclTax || 0;
            const interiorColorPrice =
                this.selectedInteriorColor?.priceExclTax || 0;
            const totalColorPrice = exteriorColorPrice + interiorColorPrice;

            await this.handleArvalPriceRecalculation(
                this.selectedEdition.carManufacturerCode,
                this.selectedEdition.co2Emission,
                this.selectedEdition.modelPrice,
                this.months,
                this.mileage,
                this.downPayment,
            );
        },

        prepareFormData(form, captchaSolution) {
            const formData = new FormData(form);
            if (this.selectedExteriorColor) {
                formData.append(
                    'exteriorColorSlug',
                    this.selectedExteriorColor.slug,
                );
                formData.append(
                    'exteriorColorName',
                    this.selectedExteriorColor.name,
                );
            }
            if (this.selectedInteriorColor) {
                formData.append(
                    'interiorColorSlug',
                    this.selectedInteriorColor.slug,
                );
                formData.append(
                    'interiorColorName',
                    this.selectedInteriorColor.name,
                );
            }
            if (captchaSolution) {
                formData.append(
                    'captchaSolution',
                    JSON.stringify(captchaSolution),
                );
            }
            return formData;
        },

        async handleDataSubmit(form, captchaSolution) {
            try {
                const formData = this.prepareFormData(form, captchaSolution);
                const response = await axios.post('/', formData);
                const result = response.data;

                if (result.data.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);
                    this.scrollToFirstError();
                    this.submitting = false;
                    return;
                }

                // Success
                this.formCompleted = true;
                this.errors.clear();
                window.location = result.data.redirectUrl;
                this.scrollToTop();
            } catch (e) {
                throw new Error(e);
            }
        },
    },
});
